import React, { useEffect, useState } from 'react'
import { getLocalizedDateTimeString } from '../../utils/dateUtils'
import CloseIcon from '../Icons/CloseIcon'
import PlusIcon from '../Icons/PlusIcon'
import WarningIcon from '../Icons/WarningIcon'
import InputCheckbox from '../InputCheckbox/InputCheckbox'
import InputText from '../InputText/InputText'
import SidebarModal from '../SidebarModal/SidebarModal'
import { Customer, CustomerBan } from './CustomerInfo'

interface CustomerBannedCardProps {
  customer: Customer
  onBanToggle: (isBanned: boolean) => void
  onBanReasonChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onBanReasonsReset: () => void
  isSavingBanReasons: boolean
  banError: boolean
}

const banReasonTexts = {
  customerHasNonpayment: 'Utebliven betalning',
  customerHasMismanagedWeighedItems: 'Felaktig hantering viktvaror',
  customerHasLetOthersIntoStore: 'Släppt in obehöriga',
  customerHasVandalized: 'Vandalisering',
}

const BAN_REASON_DEFAULT_MESSAGE = 'Ingen vald'

const CustomerBannedCard = ({ customer, onBanToggle, onBanReasonChange, onBanReasonsReset, isSavingBanReasons, banError }: CustomerBannedCardProps) => {
  const [displayBanModal, setDisplayBanModal] = useState(false)
  const [banReasonButtonMessage, setBanReasonButtonMessage] = useState(BAN_REASON_DEFAULT_MESSAGE)

  useEffect(() => {
    if(!customer.customerBan) {
      setBanReasonButtonMessage(BAN_REASON_DEFAULT_MESSAGE)
      return
    }

    const activeBanReasons = Object.entries(banReasonTexts)
      .filter(([key]) => customer.customerBan?.[key as keyof CustomerBan])
      .map(([, value]) => value)

    if (activeBanReasons.length === 0) {
      setBanReasonButtonMessage(BAN_REASON_DEFAULT_MESSAGE)      
    }
    else if (activeBanReasons.length === 1) {
      setBanReasonButtonMessage(activeBanReasons[0])
    }
    else {
      setBanReasonButtonMessage(`${activeBanReasons[0]}, +${activeBanReasons.length - 1}`)
    }

  }, [customer.customerBan])

  return (
    <div className='items-center'>
      <div className='flex items-center pt-4'>
        <label className='customer-info__ban-text my-0 ml-0 mr-2'>Spärra kund</label>
        <div className='ml-auto'>
          <InputCheckbox
            id='ban-checkbox'
            checked={!!customer.customerBan}
            onCheckChanged={(e) => onBanToggle(e.target.checked)}
          />
        </div>
      </div>

      {customer.customerBan && (
        <>
          <hr className='my-4'></hr>
          <div className='flex'>
            <div>
              <div className='customer-info__ban-text'>Spärrad {getLocalizedDateTimeString(customer.customerBan.createdAt, false)}</div>
              <div className='customer-info__ban-text'>{customer.customerBan.updatedByStoreName}</div>
              {customer.customerBan.updatedByMainStoreName && <div className='customer-info__ban-subtext'>{customer.customerBan.updatedByMainStoreName}</div>}
            </div>
            <div className='ml-auto content-center'>
              <WarningIcon size='24px' />
            </div>
          </div>
          <hr className='my-4' />
          <label htmlFor='ban-modal-button' className='customer-info__ban-text-color select-none mb-1 inline-block'>Anledning till spärrning</label>
          <InputText
            value={banReasonButtonMessage}
            icon={<PlusIcon size='24px' />} 
            onChange={() => null}
            onClick={() => setDisplayBanModal(!displayBanModal)}
            className='cursor-pointer outline-hidden'
            id='ban-modal-button'
            readonly
          />

          <SidebarModal show={displayBanModal}>
            <div className='border-bottom flex py-3 px-5'>
              <div className='filter-title select-none'>Spärra kund</div>
              <div className='close-button ml-auto' onClick={() => setDisplayBanModal(false)}><CloseIcon size='20px' /></div>
            </div>
            <div className='px-5 mb-3'>
              <div className='filter-subtitle mt-6'>Anledning till spärrning</div>
              <hr className='mt-4'/>
              <InputCheckbox
                label={banReasonTexts.customerHasNonpayment}
                id='customerHasNonpayment'
                name='customerHasNonpayment'
                checked={customer.customerBan.customerHasNonpayment}
                onCheckChanged={onBanReasonChange}
                className='py-4'
                disabled={isSavingBanReasons}
              />
              <hr/>
              <InputCheckbox
                label={banReasonTexts.customerHasMismanagedWeighedItems}
                id='customerHasMismanagedWeighedItems'
                name='customerHasMismanagedWeighedItems'
                checked={customer.customerBan.customerHasMismanagedWeighedItems}
                onCheckChanged={onBanReasonChange}
                className='py-4'
                disabled={isSavingBanReasons}
              />
              <hr/>
              <InputCheckbox
                label={banReasonTexts.customerHasLetOthersIntoStore}
                id='customerHasLetOthersIntoStore'
                name='customerHasLetOthersIntoStore'
                checked={customer.customerBan.customerHasLetOthersIntoStore}
                onCheckChanged={onBanReasonChange}
                className='py-4'
                disabled={isSavingBanReasons}
              />
              <hr/>
              <InputCheckbox
                label={banReasonTexts.customerHasVandalized}
                id='customerHasVandalized'
                name='customerHasVandalized'
                checked={customer.customerBan.customerHasVandalized}
                onCheckChanged={onBanReasonChange}
                className='py-4'
                disabled={isSavingBanReasons}
              />
              <hr/>
            </div>
            <div className='filter-footer mt-auto border-top sticky bottom-0'>
              <div className='flex p-4 w-full'>
                <button type="button" className='filter-reset-button button button-secondary grow mr-1 xl:mr-0' onClick={onBanReasonsReset}>Rensa</button>
                <button type="button" className='filter-reset-button button grow ml-1' onClick={() => setDisplayBanModal(false)}>Klar</button>
              </div>
            </div>
          </SidebarModal>
        </>
      )}

      {banError && <p className='error px-3 text-right'>Det gick inte att spärra kunden</p>}
    </div>
  )
}

export default CustomerBannedCard